import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productMediaService } from '../../services/product-media.service';
import {
  ProductMedia,
  UploadMediaData,
  UpdateMediaData,
  MediaParams,
  BulkDeleteData,
} from '../../types/product-media.types';

interface ProductMediaState {
  items: ProductMedia[];
  loading: boolean;
  error: string | null;
  actionLoading: boolean;
  totalItems: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
}

const initialState: ProductMediaState = {
  items: [],
  loading: false,
  error: null,
  actionLoading: false,
  totalItems: 0,
  currentPage: 1,
  totalPages: 1,
  hasMore: false,
};

// Async thunks
export const fetchMedia = createAsyncThunk(
  'productMedia/fetchMedia',
  async (params?: MediaParams) => {
    const response = await productMediaService.getMedia(params);
    if (response.success) {
      return response.data;
    }
    throw new Error(response.error?.message || 'Failed to fetch media');
  }
);

export const uploadMedia = createAsyncThunk(
  'productMedia/uploadMedia',
  async (data: UploadMediaData) => {
    const response = await productMediaService.upload(data);
    if (response.success) {
      return response.data.items;
    }
    throw new Error(response.error?.message || 'Failed to upload media');
  }
);

export const updateMedia = createAsyncThunk(
  'productMedia/updateMedia',
  async ({ id, data }: { id: string; data: UpdateMediaData }) => {
    const response = await productMediaService.update(id, data);
    if (response.success) {
      return response.data.item;
    }
    throw new Error(response.error?.message || 'Failed to update media');
  }
);

export const deleteMedia = createAsyncThunk(
  'productMedia/deleteMedia',
  async (id: string) => {
    const response = await productMediaService.remove(id);
    if (response.success) {
      return id;
    }
    throw new Error(response.error?.message || 'Failed to delete media');
  }
);

export const bulkDeleteMedia = createAsyncThunk(
  'productMedia/bulkDeleteMedia',
  async (data: BulkDeleteData) => {
    const response = await productMediaService.bulkDelete(data);
    if (response.success) {
      return data.ids;
    }
    throw new Error(response.error?.message || 'Failed to delete media');
  }
);

const productMediaSlice = createSlice({
  name: 'productMedia',
  initialState,
  reducers: {
    clearMedia: (state) => {
      state.items = [];
      state.totalItems = 0;
      state.currentPage = 1;
      state.totalPages = 1;
      state.hasMore = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch media
    builder.addCase(fetchMedia.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMedia.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.items;
      state.totalItems = action.payload.pagination.total;
      state.currentPage = action.payload.pagination.page;
      state.totalPages = action.payload.pagination.totalPages;
      state.hasMore = action.payload.pagination.page < action.payload.pagination.totalPages;
    });
    builder.addCase(fetchMedia.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch media';
    });

    // Upload media
    builder.addCase(uploadMedia.pending, (state) => {
      state.actionLoading = true;
      state.error = null;
    });
    builder.addCase(uploadMedia.fulfilled, (state, action) => {
      state.actionLoading = false;
      // action.payload is the array of uploaded media items
      const uploadedMedia = Array.isArray(action.payload) ? action.payload : [];
      state.items = [...uploadedMedia, ...state.items];
      state.totalItems += uploadedMedia.length;
    });
    builder.addCase(uploadMedia.rejected, (state, action) => {
      state.actionLoading = false;
      state.error = action.error.message || 'Failed to upload media';
    });

    // Update media
    builder.addCase(updateMedia.pending, (state) => {
      state.actionLoading = true;
      state.error = null;
    });
    builder.addCase(updateMedia.fulfilled, (state, action) => {
      state.actionLoading = false;
      const index = state.items.findIndex(m => m.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    });
    builder.addCase(updateMedia.rejected, (state, action) => {
      state.actionLoading = false;
      state.error = action.error.message || 'Failed to update media';
    });

    // Delete media
    builder.addCase(deleteMedia.pending, (state) => {
      state.actionLoading = true;
      state.error = null;
    });
    builder.addCase(deleteMedia.fulfilled, (state, action) => {
      state.actionLoading = false;
      state.items = state.items.filter(m => m.id !== action.payload);
      state.totalItems -= 1;
    });
    builder.addCase(deleteMedia.rejected, (state, action) => {
      state.actionLoading = false;
      state.error = action.error.message || 'Failed to delete media';
    });

    // Bulk delete media
    builder.addCase(bulkDeleteMedia.pending, (state) => {
      state.actionLoading = true;
      state.error = null;
    });
    builder.addCase(bulkDeleteMedia.fulfilled, (state, action) => {
      state.actionLoading = false;
      state.items = state.items.filter(m => !action.payload.includes(m.id));
      state.totalItems -= action.payload.length;
    });
    builder.addCase(bulkDeleteMedia.rejected, (state, action) => {
      state.actionLoading = false;
      state.error = action.error.message || 'Failed to bulk delete media';
    });
  },
});

export const { clearMedia, clearError } = productMediaSlice.actions;
export default productMediaSlice.reducer;
